/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
/* tslint:disable: no-duplicate-imports */
import * as React from 'react';

import {
    ICartlinesViewProps,
    ICartResources,
    ICartViewProps
} from '@msdyn365-commerce-modules/cart';

import { ICartLineItemViewProps } from '@msdyn365-commerce-modules/cart/dist/types/modules/cart/components/cart-line-items-promotion';
import {Button, getPayloadObject, getTelemetryAttributes, ITelemetryContent, Node, TelemetryConstant} from '@msdyn365-commerce-modules/utilities';

const CartView: React.FC<ICartViewProps> = (props: ICartViewProps) => (
    <div className={props.className} id={props.id} {...props.renderModuleAttributes(props)}>
        {props.title}
        {props.promotionOptions && _renderPromotions(props.promotionOptions)}
        {props.multiplePickUpEnabled ? _renderCartLinesGroup(props, props.resources) :
            <Node {...props.CartlinesWrapper}>
            {_renderCartlines(props.cartlines, props.resources, props.storeSelector, props.backToShoppingButton, props.waitingComponent, props.cartLoadingStatus,
                              props.cartDataResult, props.checkoutAsSignInUserButton)}
                { /* props.orderSummaryHeading &&
                <Node {...props.OrderSummaryWrapper}>
                    {props.checkoutAsSignInUserButton}
                </Node>
                */}
            </Node>
        }
        {props.storeSelector}
    </div>
);

const _renderPromotions = (promotions: ICartLineItemViewProps): JSX.Element | undefined => {
    return (
        <React.Fragment>
            <Node {...promotions.promotionMessageWrapper} >
                {promotions.promotionMessage}
                {promotions.promotionSeeAllLink}
                {promotions.promotionDialog}
            </Node>

        </React.Fragment>
    );

};

const _renderCartlines = (cartLines: ICartlinesViewProps[] | undefined, resources: ICartResources, storeSelector: React.ReactNode | undefined,
                          backToShoppingButton: React.ReactNode, waitingComponent: React.ReactNode, cartLoadingStatus: string,cartDataResult: boolean, checkoutAsSignInUserButton?: React.ReactNode,
                          telemetryContent?: ITelemetryContent, mulitplePickUp?: boolean, emailDeliveryModeCode?: string): JSX.Element[] | JSX.Element => {

    const { emptyCartText } = resources;

    if (cartLoadingStatus) {
        return <>{cartLoadingStatus}</>;
    }
    if (cartLines) {
        return cartLines.map((cartLine, index) => {
            return (
                <div className='msc-cart-lines-item' key={index}>
                    <div className='msc-cart-lines-item__row'>
                        {cartLine.cartline}
                        <div className={'complete-order__button'}>
                            {checkoutAsSignInUserButton}
                        </div>

                    </div>
                    <div className='msc-cart-lines-item__row'>
                        {mulitplePickUp ? null :
                            _renderBOPISBlock(cartLine, resources, storeSelector, telemetryContent, emailDeliveryModeCode)}
                        {
                            mulitplePickUp ? (
                                    <Node className='msc-cart-line-group__extra-actions'>
                                        {_renderGroupBOPISBlock(cartLine, resources, storeSelector, telemetryContent, emailDeliveryModeCode)}
                                        {cartLine.remove}
                                    </Node>
                                )
                                : cartLine.addToOrderTemplate ? (
                                    <Node className='msc-cart-line__extra-actions'>
                                        {cartLine.remove}
                                    </Node>
                                ) : (
                                    <>
                                        {cartLine.remove}
                                    </>
                                )
                        }
                    </div>
                    <div className={'complete-order__button'}>
                        {checkoutAsSignInUserButton}
                    </div>
                </div>
            );
        });
    } else {
        return (cartDataResult ?
                (
                    <div className='msc-cart__empty-cart'>
                        <p className='msc-cart-line'>{emptyCartText}</p>
                        {backToShoppingButton}
                    </div>
                ) : <>{waitingComponent}</>
        );
    }
};


const _renderGroupBOPISBlock = (cartLine: ICartlinesViewProps, resources: ICartResources, storeSelector: React.ReactNode | undefined, telemetryContent?: ITelemetryContent, emailDeliveryModeCode?: string): JSX.Element | null => {
    // If it is electronic item cart line, then return null for GroupBOPISBlock.
    if (!cartLine.pickUpInStore || !storeSelector || cartLine.data?.cartline.DeliveryMode === emailDeliveryModeCode) {
        return null;
    }
    const { changeStoreDisplayText, shipInsteadDisplayText, pickItUpDisplayText } = resources;

    const isBopisSelected = cartLine.pickUpInStore.isBopisSelected;
    const toggleBopis = (event: React.MouseEvent<HTMLElement>) => { return cartLine.pickUpInStore && cartLine.pickUpInStore.callbacks.toggleBopis(!isBopisSelected); };
    const changeStore = (event: React.MouseEvent<HTMLElement>) => { return cartLine.pickUpInStore && cartLine.pickUpInStore.callbacks.toggleBopis(true); };
    const payLoad = getPayloadObject('click',telemetryContent!, TelemetryConstant.PickupInStore);
    const puckUpinStoreAttribute = getTelemetryAttributes(telemetryContent!, payLoad);

    return (
        <React.Fragment>
            {isBopisSelected ? <Button className='msc-cart-line__remove-item msc-btn' onClick={changeStore}>{changeStoreDisplayText}</Button> : null }
            <Button className='msc-cart-line__remove-item msc-btn' {...puckUpinStoreAttribute} onClick={toggleBopis}>{isBopisSelected ? shipInsteadDisplayText : pickItUpDisplayText}</Button>
        </React.Fragment>
    );
};

const _renderBOPISBlock = (cartLine: ICartlinesViewProps, resources: ICartResources, storeSelector: React.ReactNode | undefined, telemetryContent?: ITelemetryContent, emailDeliveryModeCode?: string): JSX.Element | null => {
    // If it is electronic item cart line, then return null for BOPISBlock.
    if (!cartLine.pickUpInStore || !storeSelector || cartLine.data?.cartline.DeliveryMode === emailDeliveryModeCode) {
        return null;
    }

    const { shipInsteadDisplayText, pickItUpDisplayText, changeStoreDisplayText } = resources;
    const bopisName = cartLine.data?.cartline?.ShippingAddress?.Name;
   // const bopisAddress = cartLine.data?.cartline?.ShippingAddress?.FullAddress;
    const isBopisSelected = cartLine.pickUpInStore.isBopisSelected;
    const toggleBopis = (event: React.MouseEvent<HTMLElement>) => { return cartLine.pickUpInStore && cartLine.pickUpInStore.callbacks.toggleBopis(!isBopisSelected); };
    const changeStore = (event: React.MouseEvent<HTMLElement>) => { return cartLine.pickUpInStore && cartLine.pickUpInStore.callbacks.toggleBopis(true); };
    const payLoad = getPayloadObject('click',telemetryContent!, TelemetryConstant.PickupInStore);
    const puckUpinStoreAttribute = getTelemetryAttributes(telemetryContent!, payLoad);

    return (
        <Node {...cartLine.pickUpInStore.ContainerProps} className={`msc-cart-line__bopis-container ${isBopisSelected ? "pickup" : "shipping"}`}>
                <div className={'delivery-choice__shipping'}>
                    <Button disabled={isBopisSelected ? false : true} className={`msc-cart-line__bopis-btn radio ${isBopisSelected ? "choose--shipping" : "choose--pickup"}`}
                            {...puckUpinStoreAttribute} onClick={toggleBopis} />
                    <div className={'msc-cart-line__bopis__instructions'}>
                        <h5>{shipInsteadDisplayText}</h5>
                        <p>che potrai selezionare procedendo con l’ordine</p>
                    </div>
                </div>
                <div className={'delivery-choice__pickup'}>
                    <Button  disabled={!isBopisSelected ? false : true} className={`msc-cart-line__bopis-btn radio ${isBopisSelected ? "choose--shipping" : "choose--pickup"}`}
                            {...puckUpinStoreAttribute} onClick={toggleBopis} />
                    <div className={'msc-cart-line__bopis__instructions'}>
                        <h5>Ricevi al punto di ritiro</h5>
                        {isBopisSelected ? (
                            <>
                                <p>Hai scelto di ricevere la tua auto presso il punto di ritiro:</p>
                                <p className={'msc-cart-line__bopis-name'}>{bopisName}</p>
                            </>
                        ) : (
                            <p>puoi ordinare e ricevere la tua auto scegliendo il punto di ritiro più vicino a te.</p>
                        )
                        }
                        {!isBopisSelected ? (
                            <Button className={`msc-cart-line__bopis-btn ${isBopisSelected ? "choose--shipping" : "choose--pickup"}`}
                                    {...puckUpinStoreAttribute} onClick={toggleBopis}>{pickItUpDisplayText}</Button>
                        ) : (
                            <Button className='msc-cart-line__bopis-changestore' onClick={changeStore}>{changeStoreDisplayText}</Button>
                        ) }

                    </div>
                </div>

        </Node>
    );
};


const _renderCartLinesGroup = (props: ICartViewProps, resources: ICartResources,): JSX.Element | undefined => {
    if (props.cartLinesGroup && props.cartLinesGroup.length > 0) {
        return (
            <div className='msc-cart-lines-group'>
            { props.cartLinesGroup?.map(cartlines => {
            // tslint:disable-next-line:jsx-key
            return (
                <div className='msc-cart-lines-group-wraper'>
                    {_renderCartLinesGroupHeader(cartlines[0],props.storeSelector, resources, _countItems(cartlines),props.context.actionContext.requestContext.channel?.EmailDeliveryModeCode)}
                    {_renderCartlines(cartlines, props.resources, props.storeSelector, props.backToShoppingButton, props.waitingComponent, props.cartLoadingStatus, props.cartDataResult)}
                </div>);
            })
            }
            </div>
        );
    } else {
        return (props.cartDataResult ?
            (
                <div className='msc-cartline-wraper'>
                    <div className='msc-cart__empty-cart'>
                        <p className='msc-cart-line'>{props.resources.emptyCartText}</p>
                        {props.backToShoppingButton}
                    </div>
                </div>
            ) : <>{props.waitingComponent}</>
        );
    }
};

const _renderCartLinesGroupHeader = (cartLine: ICartlinesViewProps, storeSelector: React.ReactNode | undefined, resources: ICartResources,count: number,emailDeliveryModeCode?:string) => {
    const isBopisSelected = cartLine?.pickUpInStore?.isBopisSelected;
    const { pickUpText, shippingText, emailshippingText } = resources;
    let groupTitle:string;

    groupTitle = isBopisSelected ? pickUpText: shippingText;
    groupTitle =  cartLine.data && cartLine.data.cartline.DeliveryMode === emailDeliveryModeCode ? emailshippingText: groupTitle;

    return (
        <React.Fragment>
            <div className='msc-cart-lines-group-wraper__bopis-heading'>
                {<p className={`msc-cart-lines-group-wraper__bopis-heading-${groupTitle.toLowerCase()}-icon`}/>}
                {<p className={`msc-cart-lines-group-wraper__bopis-heading-${groupTitle.toLowerCase()}`}>{groupTitle}</p>}
                {_renderCartLinesGroupTitle(cartLine, resources, count)}
            </div>
        </React.Fragment>
        );
};

const _countItems = (cartLine: ICartlinesViewProps[]): number => {
    let countItem = 0;
        countItem = cartLine.reduce((count, item) => {
                return count + (item.data?.cartline.Quantity || 0);
                // tslint:disable-next-line:align
            }, 0);
    return countItem;
};

const _renderCartLinesGroupTitle = (cartLine: ICartlinesViewProps, resources: ICartResources,count: number) => {
    const isBopisSelected = cartLine?.pickUpInStore?.isBopisSelected;
    const { itemLabel, itemsLabel } = resources;
    const suffix = count > 1 ? itemsLabel : itemLabel;
    return (
        <React.Fragment>
            <Node className='msc-cart-lines-group-wraper__bopis-heading-title'>
    {isBopisSelected ? <p className='msc-cart-lines-group-wraper__bopis-heading-title-st'>{cartLine?.pickUpInStore?.deliveryOption}{', '}{cartLine?.pickUpInStore?.orgUnitName}</p>: null}
    {<p className='msc-cart-lines-group-wraper__bopis-heading-title-ct'>({count} {suffix})</p>}
            </Node>
        </React.Fragment>
        );
};

export default CartView;